// Site language. Spanish (/es) and Brazilian Portuguese (/pt) started in Oct
// 2026: Spanish-speaking countries were 17.6% of Google clicks but ~9% of
// charges, Brazil 4.2% of clicks.
//
// The URL decides the language: Next i18n routing (next.config.mjs) serves
// every page at /, /es/... and /pt/..., server-rendered in that language, and
// next/link keeps visitors inside their prefix. middleware.js sends people
// whose saved choice (jm_lang cookie) or browser language is Spanish or
// Portuguese from the unprefixed URL to theirs; crawlers are never redirected.
import React, { createContext, useContext, useEffect, useMemo } from "react";
import { useRouter } from "next/router";
import {
  en,
  es,
  pt,
  fr,
  de,
  ja,
  ko,
  id,
  tr,
  type MessageKey,
} from "./messages";

export const LOCALES = [
  "en",
  "es",
  "pt",
  "fr",
  "de",
  "ja",
  "ko",
  "id",
  "tr",
] as const;
export type Locale = (typeof LOCALES)[number];
export type { MessageKey };

// Per locale:
// - hreflang / htmlLang / og: SEO tags. Each language is offered to all its
//   speakers ("es", "pt", "fr"), though es is written for Latin America and pt
//   for Brazil.
// - intl: number/date formatting (Intl, toLocaleString).
// - stripe: Stripe Elements `locale` (undefined = Stripe's "auto").
// - dayjs: dayjs locale name (import "dayjs/locale/<name>" where used).
export const LOCALE_META: Record<
  Locale,
  {
    hreflang: string;
    htmlLang: string;
    og: string;
    intl: string;
    stripe?: string;
    dayjs: string;
  }
> = {
  en: {
    hreflang: "en",
    htmlLang: "en",
    og: "en_US",
    intl: "en-US",
    dayjs: "en",
  },
  es: {
    hreflang: "es",
    htmlLang: "es",
    og: "es_LA",
    intl: "es-419",
    stripe: "es-419",
    dayjs: "es",
  },
  pt: {
    hreflang: "pt",
    htmlLang: "pt-BR",
    og: "pt_BR",
    intl: "pt-BR",
    stripe: "pt-BR",
    dayjs: "pt-br",
  },
  fr: {
    hreflang: "fr",
    htmlLang: "fr",
    og: "fr_FR",
    intl: "fr-FR",
    stripe: "fr",
    dayjs: "fr",
  },
  de: {
    hreflang: "de",
    htmlLang: "de",
    og: "de_DE",
    intl: "de-DE",
    stripe: "de",
    dayjs: "de",
  },
  ja: {
    hreflang: "ja",
    htmlLang: "ja",
    og: "ja_JP",
    intl: "ja-JP",
    stripe: "ja",
    dayjs: "ja",
  },
  ko: {
    hreflang: "ko",
    htmlLang: "ko",
    og: "ko_KR",
    intl: "ko-KR",
    stripe: "ko",
    dayjs: "ko",
  },
  id: {
    hreflang: "id",
    htmlLang: "id",
    og: "id_ID",
    intl: "id-ID",
    stripe: "id",
    dayjs: "id",
  },
  tr: {
    hreflang: "tr",
    htmlLang: "tr",
    og: "tr_TR",
    intl: "tr-TR",
    stripe: "tr",
    dayjs: "tr",
  },
};

export const intlLocale = (l: Locale) => LOCALE_META[l].intl;

const MESSAGES: Record<Locale, Partial<Record<MessageKey, string>>> = {
  en,
  es,
  pt,
  fr,
  de,
  ja,
  ko,
  id,
  tr,
};

// A missing translation falls back to English rather than showing a key.
export function translate(
  locale: Locale,
  key: MessageKey,
  vars?: Record<string, string | number>,
) {
  let s = MESSAGES[locale]?.[key] ?? en[key] ?? key;
  if (vars)
    for (const [k, v] of Object.entries(vars))
      s = s.split(`{${k}}`).join(String(v));
  return s;
}

export function asLocale(l: string | undefined | null): Locale {
  return (LOCALES as readonly string[]).includes(l || "")
    ? (l as Locale)
    : "en";
}

// Locale implied by a path: /es, /es/... -> "es". Anything else is English.
export function localeFromPath(path: string | undefined | null): Locale {
  const first = /^\/([a-z]{2})(?:[/?#]|$)/.exec(path || "")?.[1];
  return asLocale(first);
}

// Saved language choice, read by middleware.js. Set by the footer switcher.
export const LANG_COOKIE = "jm_lang";

interface Ctx {
  locale: Locale;
  // True on /es and /pt pages (kept for callers that branch on it).
  fromUrl: boolean;
  // Saves the choice and moves to the same page in that language.
  setLocale: (l: Locale) => void;
}
const LocaleContext = createContext<Ctx>({
  locale: "en",
  fromUrl: false,
  setLocale: () => {},
});

// For code outside React (e.g. a helper that opens a modal with a message).
// Kept in step by LocaleProvider; "en" on the server.
let currentLocale: Locale = "en";
export const getCurrentLocale = () => currentLocale;

export function LocaleProvider({
  urlLocale,
  children,
}: {
  urlLocale: Locale;
  children: React.ReactNode;
}) {
  const router = useRouter();
  const value = useMemo<Ctx>(
    () => ({
      locale: urlLocale,
      fromUrl: urlLocale !== "en",
      setLocale: (l) => {
        try {
          document.cookie = `${LANG_COOKIE}=${l}; path=/; max-age=${60 * 60 * 24 * 365}; samesite=lax`;
        } catch {
          /* cookies blocked */
        }
        if (l !== urlLocale)
          router.push(router.asPath, undefined, { locale: l });
      },
    }),
    [urlLocale, router],
  );
  useEffect(() => {
    currentLocale = urlLocale;
  }, [urlLocale]);
  return (
    <LocaleContext.Provider value={value}>{children}</LocaleContext.Provider>
  );
}

export const useLocale = () => useContext(LocaleContext);

// t() for the page's locale. Pass `fixed` for content that must stay in one
// language whatever the URL, e.g. a landing page's own copy.
export function useT(fixed?: Locale) {
  const { locale } = useLocale();
  const l = fixed ?? locale;
  return useMemo(
    () => (key: MessageKey, vars?: Record<string, string | number>) =>
      translate(l, key, vars),
    [l],
  );
}
